import axios from "axios";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import "../styles/AdminDashboard.css";
import "../styles/StudentDashboard.css";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API = "https://stu168523.webhosting.arden.ac.uk";

export default function AdminDashboard() {
  const token = localStorage.getItem("token");
  const userName = localStorage.getItem("name");

  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [results, setResults] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null);

  const [newCategory, setNewCategory] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");

  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    options: ["", "", "", ""],
    correctOption: 0,
  });

  /* ================= LOAD DATA ================= */

  const loadCategories = async () => {
    const res = await axios.get(`${API}/api/categories`);
    setCategories(res.data);
  };

  const loadQuestions = async (categoryId) => {
    const res = await axios.get(`${API}/api/questions/category/${categoryId}`);
    setQuestions(res.data);
  };

  const loadResults = async () => {
    const res = await axios.get(`${API}/api/results/admin/all`);
    setResults(res.data);
  };

  useEffect(() => {
    loadCategories();
    loadResults();
  }, []);

  /* ================= QUESTION CRUD ================= */

  const addQuestion = async () => {
    if (!selectedCategory) return;

    await axios.post(
      `${API}/api/questions`,
      {
        ...newQuestion,
        categoryId: selectedCategory._id,
      },
      { headers: { authorization: token } }
    );

    setNewQuestion({
      questionText: "",
      options: ["", "", "", ""],
      correctOption: 0,
    });

    loadQuestions(selectedCategory._id);
  };

  const deleteQuestion = async (id) => {
    await axios.delete(`${API}/api/questions/${id}`, {
      headers: { authorization: token },
    });
    loadQuestions(selectedCategory._id);
  };

  const startEditCategory = (cat) => {
    setEditingCategoryId(cat._id);
    setEditingCategoryName(cat.name);
  };

  const updateCategory = async () => {
    await axios.put(
      `${API}/api/categories/${editingCategoryId}`,
      { name: editingCategoryName },
      { headers: { authorization: token } }
    );

    setEditingCategoryId(null);
    setEditingCategoryName("");
    loadCategories();
  };

  const startEditQuestion = (q) => {
    setEditingQuestionId(q._id);
    setNewQuestion({
      questionText: q.questionText,
      options: [...q.options],
      correctOption: q.correctOption,
    });
  };

  const updateQuestion = async () => {
    await axios.put(
      `${API}/api/questions/${editingQuestionId}`,
      newQuestion,
      { headers: { authorization: token } }
    );

    setEditingQuestionId(null);
    setNewQuestion({
      questionText: "",
      options: ["", "", "", ""],
      correctOption: 0,
    });

    loadQuestions(selectedCategory._id);
  };

  /* ================= LOGOUT ================= */

  const logout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  /* ================= CHART DATA ================= */

  const chartData = {
    labels: results.map((r) => r.categoryId.name),
    datasets: [
      {
        label: "Scores",
        data: results.map((r) => r.score),
      },
    ],
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;

    await axios.post(
      `${API}/api/categories`,
      { name: newCategory },
      { headers: { authorization: token } }
    );

    setNewCategory("");
    loadCategories();
  };

  const deleteCategory = async (id) => {
    await axios.delete(`${API}/api/categories/${id}`, {
      headers: { authorization: token },
    });

    if (selectedCategory?._id === id) {
      setSelectedCategory(null);
      setQuestions([]);
    }

    loadCategories();
  };

  /* ================= RENDER ================= */

  return (
    <div className="admin-dashboard">
      <div className="student-navbar">
        <div className="student-logo">🎓 QuizLMS</div>

        <div className="student-nav-right">
          <span className="student-name">👤 {userName}</span>

          <button className="logout-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-sidebar">
          <h3>Categories</h3>

          <div className="admin-add-category">
            <input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="New category name"
            />
            <button onClick={addCategory}>Add</button>
          </div>

          <ul>
            {categories.map((cat) => (
              <li key={cat._id}>
                <button
                  className={
                    selectedCategory?._id === cat._id ? "active" : ""
                  }
                  onClick={() => {
                    setSelectedCategory(cat);
                    loadQuestions(cat._id);
                  }}
                >
                  {cat.name}
                </button>

                <div className="admin-cat-actions">
                  <button onClick={() => startEditCategory(cat)}>Edit</button>
                  <button onClick={() => deleteCategory(cat._id)}>Del</button>
                </div>
              </li>
            ))}
          </ul>

          {editingCategoryId && (
            <div className="admin-edit-category">
              <input
                value={editingCategoryName}
                onChange={(e) => setEditingCategoryName(e.target.value)}
              />
              <button onClick={updateCategory}>Save</button>
              <button
                onClick={() => {
                  setEditingCategoryId(null);
                  setEditingCategoryName("");
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="admin-main">
          <h2>Admin Dashboard</h2>

          {selectedCategory && (
            <>
              <h3>Questions for: {selectedCategory.name}</h3>

              <div className="admin-add-question">
                <input
                  value={newQuestion.questionText}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, questionText: e.target.value })
                  }
                  placeholder="Question text"
                />

                {newQuestion.options.map((opt, idx) => (
                  <input
                    key={idx}
                    value={opt}
                    onChange={(e) => {
                      const updated = [...newQuestion.options];
                      updated[idx] = e.target.value;
                      setNewQuestion({ ...newQuestion, options: updated });
                    }}
                    placeholder={`Option ${idx + 1}`}
                  />
                ))}

                <select
                  value={newQuestion.correctOption}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      correctOption: Number(e.target.value),
                    })
                  }
                >
                  <option value={0}>Correct: Option 1</option>
                  <option value={1}>Correct: Option 2</option>
                  <option value={2}>Correct: Option 3</option>
                  <option value={3}>Correct: Option 4</option>
                </select>

                {editingQuestionId ? (
                  <button onClick={updateQuestion}>Update Question</button>
                ) : (
                  <button onClick={addQuestion}>Add Question</button>
                )}

                {editingQuestionId && (
                  <button
                    onClick={() => {
                      setEditingQuestionId(null);
                      setNewQuestion({
                        questionText: "",
                        options: ["", "", "", ""],
                        correctOption: 0,
                      });
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              <div className="admin-question-list">
                {questions.map((q) => (
                  <div key={q._id} className="admin-question-card">
                    <p>
                      <b>Q:</b> {q.questionText}
                    </p>
                    <ul>
                      {q.options.map((o, i) => (
                        <li key={i}>
                          {i === q.correctOption ? <b>{o}</b> : o}
                        </li>
                      ))}
                    </ul>

                    <div className="admin-q-actions">
                      <button onClick={() => startEditQuestion(q)}>Edit</button>
                      <button onClick={() => deleteQuestion(q._id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <h3>All Results</h3>
          <table className="admin-results-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Category</th>
                <th>Score</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              {results.map((r) => (
                <tr key={r._id}>
                  <td>{r.userId?.name}</td>
                  <td>{r.categoryId?.name}</td>
                  <td>
                    {r.score}/{r.totalQuestions}
                  </td>
                  <td>{new Date(r.attemptedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Performance Analytics</h3>
          <Bar data={chartData} />
        </div>
      </div>
    </div>
  );
}
